import { Component, ChangeDetectionStrategy, input, output, booleanAttribute, computed, inject } from '@angular/core';
import { SafeUrl, DomSanitizer } from '@angular/platform-browser';
import { DnsCheckStatuses } from '@zerops/models/http-routing';
import { ServiceStackStatuses } from '@zerops/models/service-stack';

export interface RoutingLocationLinkClick {
  href: string;
  anchor: HTMLElement;
}

@Component({
  selector: 'zui-routing-location-item',
  templateUrl: './routing-location-item.component.html',
  styleUrl: './routing-location-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoutingLocationItemComponent {

  // # Deps
  #saniziter = inject(DomSanitizer);

  // # Inputs

  internalProtocol = input<string>();
  path = input<string>();
  port = input<string>();
  dnsCheckStatus = input<DnsCheckStatuses>();
  isSynced = input<boolean>();
  serviceStackId = input<string>();
  serviceStackStatus = input<ServiceStackStatuses>();
  serviceStackName = input<string>();
  type = input<'stack' | 'other' | 'project'>('stack');
  serviceStackTypeFullname = input<string>();
  copyToClipboardTooltipTranslation = input<string>();
  sslEnabled = input<boolean>();
  activeSsl = input<boolean>();
  routingType = input<'http' | 'port'>();
  portTextAlign = input<'left' | 'right'>('left');
  pathTextAlign = input<'left' | 'right'>('left');

  disableLink = input(false, { transform: booleanAttribute });
  showDomainPlaceholder = input(false, { transform: booleanAttribute });
  /** Emit `linkClick` instead of following the link, so the host can show
   *  something (e.g. login credentials) before the user leaves. */
  interceptLink = input(false, { transform: booleanAttribute });

  domains = input<string[]>();

  // # Outputs
  linkClick = output<RoutingLocationLinkClick>();

  // # Data
  // -- sync
  dnsCheckStatuses = DnsCheckStatuses;
  serviceStackStatuses = ServiceStackStatuses;
  // portRoutingLocation: { path: string; } = undefined;
  // safeDomainPlaceholder = this._saniziter.bypassSecurityTrustHtml('{domain}');

  // -- angular
  formattedDomains = computed(() => {
    const domains = this.domains() ?? [];

    if (domains.length === 0) return '{domain}';
    if (domains.length <= 3) return domains.join(', ');
    return `${domains.slice(0, 2).join(', ')} +${domains.length - 2}`;
  });

  safeHrefRaw = computed(() => {
    const routingType = this.routingType();
    const path = this.path() ?? '';
    const activeSsl = !!this.activeSsl();

    if (!path) return '';

    if (routingType === 'http') {
      const shouldForceHttps = activeSsl || path.includes('.zerops.io');
      return shouldForceHttps ? path.replace('http://', 'https://') : path;
    }

    return `http://${path}`;
  });

  safeHref = computed<SafeUrl>(() =>
    this.#saniziter.bypassSecurityTrustUrl(this.safeHrefRaw())
  );

  isWildcard = computed(() => (this.path() ?? '').includes('*'));

  onLinkClick(event: MouseEvent, anchor: HTMLAnchorElement) {
    if (!this.interceptLink()) return;

    event.preventDefault();
    this.linkClick.emit({ href: this.safeHrefRaw(), anchor });
  }

}
